import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { ButtonUI, ModalUI } from 'sonata-react-components';
import { CampaignPlan } from 'sonata-static-storage/lib/schemas/multiFlight/campaignPlan.schema';
import { InsertionOrder } from 'sonata-static-storage/lib/schemas/multiFlight/insertionOrder.schema';

import { AdvertiserSelector } from '@/components/common/AdvertiserSelector';
import { CampaignPlanSelector } from '@/components/common/CampaignPlanSelector';
import { InsertionOrderSelector } from '@/components/common/InsertionOrderSelector';
import { useModals } from '@/router';

import './modal.scss';

import type { MediaAdvertiserUserInterface } from 'sonata-static-storage/lib/schemas/mediaInfo/mediaAdvertiserUser.schema';

type State = {
  advertiser?: MediaAdvertiserUserInterface;
  campaignPlan?: CampaignPlan;
  insertionOrder?: InsertionOrder;
};

type SelectorEntityKey = keyof State;

export default function LineItemModal() {
  const { formatMessage } = useIntl();
  const modals = useModals();
  const location = useLocation();
  const modalRef: any = useRef();
  const createToken = useRef({} as Record<string, any>);

  const [{ advertiser, campaignPlan, insertionOrder }, setState] = useState<State>({});

  useEffect(() => {
    modalRef?.current?.openModal();
  }, [modalRef]);

  useEffect(() => {
    return () => {
      createToken.current?.cancel?.();
    };
  }, []);

  const handleCreateIOButtonClick = () => {
    modals.open('/campaigns/new/lineItemSettingsModal', {
      at: {
        pathname: '/campaigns',
        search: location.search
      },
      state: {
        campaignPlanId: campaignPlan?._id,
        insertionOrderId: insertionOrder?._id,
        modalId: 'lineItem-modal',
        mode: 'create',
        showSelectorStep: false
      }
    });
  };

  const handleEntitySelectorChange = (
    entityType: SelectorEntityKey,
    entitySelected: MediaAdvertiserUserInterface | CampaignPlan | InsertionOrder
  ) => {
    setState(prevState => ({
      ...prevState,
      [entityType]: entitySelected,
      ...(entityType === 'advertiser' && {
        campaignPlan: undefined,
        insertionOrder: undefined
      }),
      ...(entityType === 'campaignPlan' && {
        insertionOrder: undefined
      })
    }));
  };

  const handleClose = () =>
    modals.close({
      at: {
        pathname: '/campaigns',
        search: location.search
      }
    });
  return (
    <ModalUI
      centerButton={
        <ButtonUI
          disabled={!advertiser || !insertionOrder || !campaignPlan}
          icon="fa fa-arrow-right"
          iconPosition="right"
          id="ioSettingsBtn"
          onClick={handleCreateIOButtonClick}
          shape="squareRounded"
          text={formatMessage({
            defaultMessage: 'Got to Line Item Creation',
            id: 'campaignPlans.modal.lineItemModalButton'
          })}
          type="primary"
        />
      }
      closeModalCallback={handleClose}
      description={formatMessage({
        defaultMessage:
          'Select the associated Advertiser, Campaign Plan and Insertion Order to inherit their settings.',
        id: 'feesBySolutionWizard.entitySelector.wizardDescription'
      })}
      ref={modalRef}
      title={formatMessage({
        defaultMessage: 'Create Line Item',
        id: 'campaignPlans.modal.title.createLineItem'
      })}
      width="805px"
    >
      <div className="lineItemModal">
        <div className="lineItemModal__row">
          <div className="entitySelectorsGrid">
            <div>
              <AdvertiserSelector
                id="insertionOrderModalAdvertiserSelector"
                onChange={adv => handleEntitySelectorChange('advertiser', adv)}
              />
            </div>
            {advertiser && (
              <div className="campaignPlanSelectorContainer">
                <CampaignPlanSelector
                  advertiserId={advertiser?._id}
                  id="lineItemModalCampaignPlanSelector"
                  key={advertiser?._id}
                  onChange={cp => handleEntitySelectorChange('campaignPlan', cp)}
                />
              </div>
            )}
            {campaignPlan && (
              <div className="campaignPlanSelectorContainer">
                <InsertionOrderSelector
                  cpId={campaignPlan?._id}
                  id="lineItemModalIOSelector"
                  key={campaignPlan?._id}
                  onChange={io => handleEntitySelectorChange('insertionOrder', io)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalUI>
  );
}
